import React, { useState } from 'react';
import styled from '@emotion/styled';
import DocumentSelector from './DocumentSelector';
import { colors } from '../theme/colors';
import { optimizeFileForUpload } from '../utils/fileOptimization';

const UploadContainer = styled.div`
  border: ${props => props.hasAvailableDocs ? 'none' : `2px dashed ${colors.dropzoneBorder}`};
  border-radius: 8px;
  padding: ${props => props.hasAvailableDocs ? '0.75rem' : '1.5rem'};
  text-align: center;
  transition: all 0.2s;
  cursor: ${props => props.hasAvailableDocs ? 'default' : 'pointer'};
  background-color: ${props => props.hasAvailableDocs ? 'transparent' : colors.dropzoneBackground};
  min-height: ${props => props.hasFile ? '120px' : 'auto'};
  
  &:hover {
    border-color: ${props => props.hasAvailableDocs ? 'transparent' : colors.dropzoneBorderActive};
    background-color: ${props => props.hasAvailableDocs ? 'transparent' : colors.dropzoneHoverBackground};
  }
  
  ${props => props.hasFile && `
    border-color: ${colors.dropzoneBorder};
    background-color: ${colors.dropzoneHasFileBackground};
    border-style: solid;
    
    &:hover {
      background-color: ${colors.dropzoneHasFileBackground};
      opacity: 0.9;
    }
  `}
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${props => props.hasFile ? '#ffffff' : '#2c3e50'};
  text-shadow: ${props => props.hasFile ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'};
`;

const FileInput = styled.input`
  display: none;
`;

const FileInfo = styled.div`
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #7f8c8d;
`;

const FileName = styled.div`
  font-weight: 500;
  color: #000000; /* Black text for better contrast on darker background */
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.875rem; /* Smaller font size per Material Design */
  text-shadow: none; /* Remove text shadow for cleaner look */
  
  span {
    background-color: rgba(255, 255, 255, 0.9); /* More opaque background for better readability */
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    backdrop-filter: blur(4px);
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #000000; /* Ensure black text in span too */
  }
`;

const FileIcon = styled.span`
  font-size: 1.2rem;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
`;

const CompressionStatus = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: ${props => props.isCompressing ? '#eff6ff' : '#f0fdf4'};
  border: 1px solid ${props => props.isCompressing ? '#bfdbfe' : '#bbf7d0'};
  border-radius: 4px;
  font-size: 0.8rem;
  color: ${props => props.isCompressing ? '#1e40af' : '#166534'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .spinner {
    display: inline-block;
    animation: spin 1s linear infinite;
  }
`;

const FileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Required = styled.span`
  color: #e74c3c;
  margin-left: 0.25rem;
`;

function FileUpload({ 
  label, 
  fileType, 
  file, 
  onChange, 
  accept, 
  required = false,
  availableDocuments = [],
  onPreviewDocument 
}) {
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionStats, setCompressionStats] = useState(null);
  
  // Debug logging - commented out to reduce console noise
  // console.log('FileUpload props:', {
  //   label,
  //   fileType,
  //   file,
  //   hasFile: !!file,
  //   fileName: file?.name,
  //   fileType: typeof file,
  //   accept,
  //   availableDocuments: availableDocuments.length
  // });

  // Handle both File objects and string paths
  const getFileName = () => {
    if (!file) return null;
    if (typeof file === 'string') {
      // Extract filename from path
      return file.split('\\').pop().split('/').pop();
    }
    return file.name;
  };

  const fileName = getFileName();

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const originalSizeMB = (selectedFile.size / 1024 / 1024).toFixed(2);
      
      // Check if file needs browser compression (TIF files or large files)
      const isTiff = selectedFile.name.toLowerCase().match(/\.tiff?$/i);
      if (selectedFile.size > 5 * 1024 * 1024 || isTiff) {
        setIsCompressing(true);
        setCompressionStats(null);
      }
      
      // Optimize file if needed (TIF files or files >5MB)
      const optimizedFile = await optimizeFileForUpload(selectedFile);
      
      // If file was compressed, show stats
      if (optimizedFile.size < selectedFile.size) {
        const compressedSizeMB = (optimizedFile.size / 1024 / 1024).toFixed(2);
        const reductionPct = (((selectedFile.size - optimizedFile.size) / selectedFile.size) * 100).toFixed(1);
        setCompressionStats({
          originalSizeMB,
          compressedSizeMB,
          reductionPct
        });
        
        // Auto-hide stats after 5 seconds
        setTimeout(() => setCompressionStats(null), 5000);
      }
      
      setIsCompressing(false);
      onChange(fileType, optimizedFile);
    }
  };

  const handleClick = () => {
    document.getElementById(`file-input-${fileType}`).click();
  };

  const handlePreview = () => {
    if (file && onPreviewDocument) {
      // Use the parent's preview handler for local files too
      onPreviewDocument(file);
    }
  };

  const handlePreviewDocument = (document) => {
    if (onPreviewDocument) {
      onPreviewDocument(document);
    }
  };

  // If we have available documents and no uploaded file, show compact view
  if (availableDocuments.length > 0 && !file) {
    return (
      <UploadContainer hasFile={false} hasAvailableDocs={true}>
        <FileInput
          id={`file-input-${fileType}`}
          type="file"
          accept={accept}
          onChange={handleFileChange}
        />
        
      </UploadContainer>
    );
  }

  return (
    <UploadContainer hasFile={!!file} hasAvailableDocs={availableDocuments.length > 0} onClick={handleClick}>
      <Label hasFile={!!file}>
        {label}
        {required && <Required>*</Required>}
      </Label>
      
      <FileInput
        id={`file-input-${fileType}`}
        type="file"
        accept={accept}
        onChange={handleFileChange}
      />
      
      {isCompressing && (
        <CompressionStatus isCompressing={true}>
          <span className="spinner">⚙️</span>
          <span>Compressing large file in browser...</span>
        </CompressionStatus>
      )}
      
      {compressionStats && (
        <CompressionStatus isCompressing={false}>
          <span>✅</span>
          <span>
            Compressed: {compressionStats.originalSizeMB}MB → {compressionStats.compressedSizeMB}MB 
            ({compressionStats.reductionPct}% reduction)
          </span>
        </CompressionStatus>
      )}
      
      {file ? (
        <>
          <FileName title={file} /* Show full path on hover */>
            <FileIcon>📄</FileIcon>
            <span>{fileName}</span>
          </FileName>
          {/* {console.log('Rendering file section:', { fileName, fileType: typeof file === 'string' ? 'path' : file.type })} */}
          {/* Show available documents even when file is uploaded */}
          {availableDocuments.length > 0 && (
            <FileActions>
              <DocumentSelector
                documents={availableDocuments}
                onPreview={handlePreviewDocument}
                triggerText="Available Documents"
                triggerIcon="📁"
              />
            </FileActions>
          )}
        </>
      ) : (
        <FileInfo>
          Click to upload or drag and drop
          <br />
          <small>Accepted: {accept}</small>
          {availableDocuments.length > 0 && (
            <FileActions>
              <DocumentSelector
                documents={availableDocuments}
                onPreview={handlePreviewDocument}
                triggerText="Available Documents"
                triggerIcon="📁"
              />
            </FileActions>
          )}
        </FileInfo>
      )}
      
    </UploadContainer>
  );
}

export default FileUpload;
